// workload-defs.js — Workload ID constants, label maps, option arrays, and
// pure query/helper functions for the workload data model.
// Extracted from script.js (Stage 2).
//
// Imports: sites + hypervisorMigrationScopes from state.js (used by migration helpers).
// No imports from config.js — this module is independent of effort calculations.

import { sites, hypervisorMigrationScopes } from './state.js';

// ── Workload ID constants ─────────────────────────────────────────────────────
export const unstructuredDataWorkloadId           = 'unstructured-data';
export const repoMigrationWorkloadId              = 'vdc-vault-migration-data';
export const kastenK10WorkloadId                  = 'kasten-k10';
export const veeamBackupForMicrosoft365WorkloadId = 'veeam-backup-microsoft-365';

export const legacyWorkloadValueAliases = {
    'microsoft-365': veeamBackupForMicrosoft365WorkloadId,
    kubernetes: kastenK10WorkloadId
};

// ── Workload label maps ───────────────────────────────────────────────────────
export const hypervisorLabels = {
    vmware: 'VMware vSphere VMs',
    'vmware-cloud-director': 'VMware Cloud Director VMs',
    hyperv: 'Microsoft Hyper-V VMs',
    nutanix: 'Nutanix AHV VMs',
    ovirt: 'oVirt VMs',
    proxmox: 'Proxmox VE VMs',
    'scale-computing-hypercore': 'Scale Computing HyperCore VMs',
    'hpe-morpheus-vm-essentials': 'HPE Morpheus VM Essentials VMs',
    kvm: 'KVM VMs'
};

export const cloudLabels = {
    'entra-id': 'Microsoft Entra ID',
    'aws-ec2': 'AWS EC2 instances',
    'aws-rds': 'AWS RDS resources',
    'aws-dynamodb': 'AWS DynamoDB tables',
    'aws-redshift': 'AWS Redshift clusters',
    'aws-redshift-serverless': 'AWS Redshift Serverless',
    'aws-efs': 'AWS EFS file systems',
    'aws-fsx': 'AWS FSx file systems',
    'aws-vpc': 'AWS VPC configurations',
    'azure-vm': 'Microsoft Azure VMs',
    'azure-sql': 'Microsoft Azure SQL databases',
    'azure-cosmosdb': 'Microsoft Azure Cosmos DB accounts',
    'azure-files': 'Microsoft Azure file shares',
    'azure-vnet': 'Microsoft Azure virtual network configurations',
    'gcp-vm': 'Google Cloud VMs',
    'gcp-sql': 'Google Cloud SQL instances',
    'gcp-spanner': 'Google Cloud Spanner instances'
};

export const saasLabels = {
    [veeamBackupForMicrosoft365WorkloadId]: 'Microsoft 365'
};

export const containerLabels = {
    [kastenK10WorkloadId]: 'Kubernetes clusters'
};

export const pluginLabels = {
    'plugin-ibm-db2':    'IBM Db2',
    'plugin-sql-server': 'Microsoft SQL Server',
    'plugin-oracle-rman':'Oracle RMAN',
    'plugin-sap-oracle': 'SAP on Oracle',
    'plugin-sap-hana':   'SAP HANA',
    'plugin-sap-maxdb':  'SAP MaxDB'
};

/** Hover hints shown as a "?" badge in the workload picker drawer.
 *  Used for plug-ins (full product name) and special workloads (protection notes). */
export const pluginFullLabels = {
    'plugin-ibm-db2':    'Veeam Plug-In for IBM Db2',
    'plugin-sql-server': 'Veeam Plug-In for Microsoft SQL Server',
    'plugin-oracle-rman':'Veeam Plug-In for Oracle RMAN',
    'plugin-sap-oracle': 'Veeam Plug-In for SAP on Oracle',
    'plugin-sap-hana':   'Veeam Plug-In for SAP HANA',
    'plugin-sap-maxdb':  'Veeam Plug-In for SAP MaxDB',
    'entra-id':          'Protected directly by VBR — no VBfAz or VB365 required'
};

export const unstructuredDataLabels = {
    [unstructuredDataWorkloadId]: 'Object Storage / NAS'
};

export const sourceWorkloadLabels = {
    ...hypervisorLabels,
    ...cloudLabels,
    ...saasLabels,
    ...containerLabels,
    ...pluginLabels,
    ...unstructuredDataLabels
};

export const agentLabels = {
    'agent-windows': 'Microsoft Windows',
    'agent-linux-kernel': 'Linux (kernel module)',
    'agent-linux-nosnap': 'Linux (nosnap)',
    'agent-aix': 'IBM AIX',
    'agent-solaris': 'Oracle Solaris',
    'agent-mac': 'Mac'
};

// ── Agent version options ─────────────────────────────────────────────────────
// Version history (last 3 major releases) per physical/agent workload type.
// Used to capture the currently-installed agent version on protected workloads.
export const agentVersionOptions = {
    'agent-windows':      ['v5', 'v6', 'v13'],
    'agent-linux-kernel': ['v5', 'v6', 'v13'],
    'agent-linux-nosnap': ['v5', 'v6', 'v13'],
    'agent-mac':          ['v1', 'v2', 'v13'],
    'agent-aix':          ['v3', 'v4', 'v13'],
    'agent-solaris':      ['v3', 'v4', 'v13'],
};

export function getAgentVersionOptions(workloadType) {
    return agentVersionOptions[workloadType] || null;
}

/** Default to the previous major version — a conservative starting assumption
 *  for an upgrade/health-check engagement. */
export function getDefaultAgentVersion(workloadType) {
    const opts = agentVersionOptions[workloadType];
    if (!opts) return null;
    return opts[1] ?? opts[0]; // middle of the 3 (previous major)
}

// ── UI option labels ──────────────────────────────────────────────────────────
export const veeamOneOptionLabels = {
    reporting: 'Customised Reporting',
    dashboards: 'Customised Dashboards',
    alarms: 'Customised Alarms'
};

export const implementationModeLabels = {
    none: 'Resident Enterprise Architect',
    'veeam-ps': 'Standard Professional Services'
};

export function getImplementationModeLabel(mode) {
    return implementationModeLabels[mode] ?? mode;
}


export const engagementTypeLabels = {
    greenfield: 'Greenfield',
    upgrade: 'Upgrade',
    'health-check': 'Health Check', // Legacy — no longer creatable in the unified canvas; retained for SOW/import compatibility
    'vdc-vault': 'Repository Migration'
};

// ── Architecture option labels ────────────────────────────────────────────────
export const architectureOptionLabels = {
    veeamVersion: {
        v11: 'v11',
        'v12.2': 'v12',
        v13: 'v13'
    },
    osPlatform: {
        windows: 'Windows',
        linux: 'Linux (VSA)'
    },
    vbrHa: {
        yes: 'Yes',
        no: 'No'
    },
    databaseType: {
        postgresql: 'PostgreSQL',
        mssql: 'MSSQL'
    },
    remoteConfigurationDatabase: {
        yes: 'Yes',
        no: 'No'
    },
    vbem: {
        yes: 'Yes',
        no: 'No'
    },
    remoteDb: {
        yes: 'Yes',
        no: 'No'
    },
    sqlAoag: {
        yes: 'Yes',
        no: 'No'
    },
    advancedRbac: {
        yes: 'Yes',
        no: 'No'
    },
    deploymentType: {
        virtual: 'Virtual',
        physical: 'Physical'
    },
    networkConstraints: {
        no: 'No',
        'wan-acceleration': 'WAN Acceleration',
        seeding: 'Seeding'
    },
    siteTopology: {
        centralized: 'Centralized / ROBO',
        distributed: 'Distributed / Multi-VBR'
    }
};

export const tapeConnectivityTypeLabels = {
    sas: 'SAS',
    'fiber-channel': 'Fiber Channel'
};

// ── Workload picker option groups ─────────────────────────────────────────────
// Groups used to render the workload picker drawer in Step 1.
// inDevelopment:true  → workload shows a "Coming Soon" badge; drives inDevelopmentWorkloadValues below.
// Labels are canonical — no asterisks; the badge handles the visual indicator.
export const workloadOptionGroups = [
    {
        label: 'Hypervisors',
        options: [
            { value: 'vmware',                        label: 'VMware vSphere VMs',              unit: 'VMs' },
            { value: 'hyperv',                        label: 'Microsoft Hyper-V VMs',           unit: 'VMs' },
            { value: 'vmware-cloud-director',         label: 'VMware Cloud Director VMs',       unit: 'VMs',       inDevelopment: true },
            { value: 'nutanix',                       label: 'Nutanix AHV VMs',                 unit: 'VMs',       inDevelopment: true },
            { value: 'ovirt',                         label: 'oVirt VMs',                       unit: 'VMs',       inDevelopment: true },
            { value: 'proxmox',                       label: 'Proxmox VE VMs',                  unit: 'VMs',       inDevelopment: true },
            { value: 'scale-computing-hypercore',     label: 'Scale Computing HyperCore VMs',   unit: 'VMs',       inDevelopment: true },
            { value: 'hpe-morpheus-vm-essentials',    label: 'HPE Morpheus VM Essentials VMs',  unit: 'VMs',       inDevelopment: true },
            { value: 'kvm',                           label: 'KVM VMs',                         unit: 'VMs',       inDevelopment: true }
        ]
    },
    {
        label: 'Unstructured Data',
        options: [
            { value: 'unstructured-data',             label: 'Object Storage / NAS',            unit: 'TB',        inDevelopment: true }
        ]
    },
    {
        label: 'Cloud',
        options: [
            { value: 'entra-id',                      label: 'Microsoft Entra ID',                              unit: 'users',        inDevelopment: true },
            { value: 'aws-ec2',                       label: 'AWS EC2 instances',                               unit: 'instances',    inDevelopment: true },
            { value: 'aws-rds',                       label: 'AWS RDS resources',                               unit: 'resources',    inDevelopment: true },
            { value: 'aws-dynamodb',                  label: 'AWS DynamoDB tables',                             unit: 'tables',       inDevelopment: true },
            { value: 'aws-redshift',                  label: 'AWS Redshift clusters',                           unit: 'clusters',     inDevelopment: true },
            { value: 'aws-redshift-serverless',       label: 'AWS Redshift Serverless',                         unit: 'namespaces',   inDevelopment: true },
            { value: 'aws-efs',                       label: 'AWS EFS file systems',                            unit: 'file systems', inDevelopment: true },
            { value: 'aws-fsx',                       label: 'AWS FSx file systems',                            unit: 'file systems', inDevelopment: true },
            { value: 'aws-vpc',                       label: 'AWS VPC configurations',                          unit: 'VPCs',         inDevelopment: true },
            { value: 'azure-vm',                      label: 'Microsoft Azure VMs',                             unit: 'VMs',          inDevelopment: true },
            { value: 'azure-sql',                     label: 'Microsoft Azure SQL databases',                   unit: 'databases',    inDevelopment: true },
            { value: 'azure-cosmosdb',                label: 'Microsoft Azure Cosmos DB accounts',              unit: 'accounts',     inDevelopment: true },
            { value: 'azure-files',                   label: 'Microsoft Azure file shares',                     unit: 'shares',       inDevelopment: true },
            { value: 'azure-vnet',                    label: 'Microsoft Azure virtual network configurations',  unit: 'VNets',        inDevelopment: true },
            { value: 'gcp-vm',                        label: 'Google Cloud VMs',                                unit: 'VMs',          inDevelopment: true },
            { value: 'gcp-sql',                       label: 'Google Cloud SQL instances',                      unit: 'instances',    inDevelopment: true },
            { value: 'gcp-spanner',                   label: 'Google Cloud Spanner instances',                  unit: 'instances',    inDevelopment: true }
        ]
    },
    {
        label: 'SaaS',
        options: [
            { value: veeamBackupForMicrosoft365WorkloadId, label: 'Microsoft 365',              unit: 'users',     inDevelopment: true }
        ]
    },
    {
        label: 'Containers',
        options: [
            { value: kastenK10WorkloadId,             label: 'Kubernetes clusters',              unit: 'clusters',  inDevelopment: true }
        ]
    },
    {
        label: 'Applications',
        options: [
            { value: 'plugin-ibm-db2',       label: 'IBM Db2',                unit: 'instances',  inDevelopment: true },
            { value: 'plugin-sql-server',    label: 'Microsoft SQL Server',   unit: 'instances',  inDevelopment: true },
            { value: 'plugin-oracle-rman',   label: 'Oracle RMAN',            unit: 'instances',  inDevelopment: true },
            { value: 'plugin-sap-oracle',    label: 'SAP on Oracle',          unit: 'instances',  inDevelopment: true },
            { value: 'plugin-sap-hana',      label: 'SAP HANA',               unit: 'instances',  inDevelopment: true },
            { value: 'plugin-sap-maxdb',     label: 'SAP MaxDB',              unit: 'instances',  inDevelopment: true }
        ]
    },
    {
        label: 'Physical',
        options: [
            { value: 'agent-windows',        label: 'Microsoft Windows',       unit: 'machines',   inDevelopment: true },
            { value: 'agent-linux-kernel',   label: 'Linux (kernel module)',    unit: 'machines',   inDevelopment: true },
            { value: 'agent-linux-nosnap',   label: 'Linux (nosnap)',           unit: 'machines',   inDevelopment: true },
            { value: 'agent-aix',            label: 'IBM AIX',                 unit: 'machines',   inDevelopment: true },
            { value: 'agent-solaris',        label: 'Oracle Solaris',           unit: 'machines',   inDevelopment: true },
            { value: 'agent-mac',            label: 'Mac',                     unit: 'machines',   inDevelopment: true }
        ]
    }
];

/** Flat map of workload value → unit string, derived from workloadOptionGroups.
 *  This is the single source of truth for quantity labels — update the unit
 *  field in workloadOptionGroups above; this map updates automatically. */
export const workloadUnitMap = Object.fromEntries(
    workloadOptionGroups.flatMap((g) => g.options.map((o) => [o.value, o.unit]))
);

// ── Job type definitions ──────────────────────────────────────────────────────
// Job types available on the canvas job list.
export const jobTypeOptions = [
    { value: 'backup',          label: 'Backup Job',          hasCount: true },
    { value: 'backup-copy',     label: 'Backup Copy Job',     hasCount: true },
    { value: 'replication',     label: 'Replication Job',     hasCount: true },
    { value: 'cdp-replication', label: 'CDP Policy',          hasCount: true },
    { value: 'surebackup',      label: 'SureBackup Job',      hasCount: true },
    { value: 'surereplica',     label: 'SureReplica Job',     hasCount: true },
];

// Workload types permitted for SureBackup jobs (Hyper-V, VMware, Windows/Linux agents only)
export const sureBackupAllowedWorkloads = new Set([
    'vmware', 'hyperv',
    'agent-windows', 'agent-linux-kernel', 'agent-linux-nosnap'
]);

// Workload types permitted for Replication Jobs (VBR supports VMware vSphere and Hyper-V only)
export const replicationAllowedWorkloads = new Set([
    'vmware', 'hyperv'
]);

// Workload types permitted for CDP Policies
//   - VMware vSphere: CDP via VMware VAIO / vSphere APIs for I/O Filtering
//   - Hyper-V: CDP Policies supported since VBR v12
//   - CDP Universal Agent: agent-based CDP for physical Windows/Linux endpoints
export const cdpAllowedWorkloads = new Set([
    'vmware', 'hyperv', 'cdp-universal-agent'
]);

// Workload types excluded from Backup Copy Jobs:
//   - AWS Redshift / Redshift Serverless: cloud-native backups only, no documented backup copy support
//   - AWS VPC configs: configuration capture, not a data-workload backup copy
//   - Azure VNets: configuration capture, not a data-workload backup copy
//   - Azure SQL / Cosmos DB / Files: backup copy semantics vary by product, not safe to claim generically
//   - GCP SQL / Spanner: backed up via native GCP capabilities, backup copy not a standardised feature
export const backupCopyExcludedWorkloads = new Set([
    'aws-redshift', 'aws-redshift-serverless', 'aws-vpc',
    'azure-sql', 'azure-cosmosdb', 'azure-files', 'azure-vnet',
    'gcp-sql', 'gcp-spanner'
]);

// All workload types supported as job/unprotected-workload targets — grouped for the drawer.
export const jobWorkloadOptions = [
    // Hypervisors
    { value: 'vmware',                        label: 'VMware vSphere VMs',                       unit: 'VMs',         group: 'Hypervisors' },
    { value: 'hyperv',                        label: 'Microsoft Hyper-V VMs',                    unit: 'VMs',         group: 'Hypervisors' },
    { value: 'vmware-cloud-director',         label: 'VMware Cloud Director VMs',                unit: 'VMs',         group: 'Hypervisors' },
    { value: 'nutanix',                       label: 'Nutanix AHV VMs',                          unit: 'VMs',         group: 'Hypervisors' },
    { value: 'proxmox',                       label: 'Proxmox VE VMs',                           unit: 'VMs',         group: 'Hypervisors' },
    { value: 'scale-computing-hypercore',     label: 'Scale Computing HyperCore VMs',            unit: 'VMs',         group: 'Hypervisors' },
    { value: 'hpe-morpheus-vm-essentials',    label: 'HPE Morpheus VM Essentials VMs',           unit: 'VMs',         group: 'Hypervisors' },
    { value: 'kvm',                           label: 'KVM VMs',                                  unit: 'VMs',         group: 'Hypervisors' },
    { value: 'ovirt',                         label: 'oVirt VMs',                                unit: 'VMs',         group: 'Hypervisors' },
    // Unstructured Data
    { value: 'unstructured-data',             label: 'Object Storage / NAS',                     unit: 'TB',          group: 'Unstructured Data' },
    // Cloud — AWS
    { value: 'aws-ec2',                       label: 'AWS EC2 instances',                        unit: 'instances',   group: 'Cloud' },
    { value: 'aws-rds',                       label: 'AWS RDS resources',                        unit: 'resources',   group: 'Cloud' },
    { value: 'aws-dynamodb',                  label: 'AWS DynamoDB tables',                      unit: 'tables',      group: 'Cloud' },
    { value: 'aws-redshift',                  label: 'AWS Redshift clusters',                    unit: 'clusters',    group: 'Cloud' },
    { value: 'aws-redshift-serverless',       label: 'AWS Redshift Serverless',                  unit: 'instances',   group: 'Cloud' },
    { value: 'aws-efs',                       label: 'AWS EFS file systems',                     unit: 'file systems', group: 'Cloud' },
    { value: 'aws-fsx',                       label: 'AWS FSx file systems',                     unit: 'file systems', group: 'Cloud' },
    { value: 'aws-vpc',                       label: 'AWS VPC configurations',                   unit: 'VPCs',        group: 'Cloud' },
    // Cloud — Microsoft (VBR-native)
    { value: 'entra-id',                      label: 'Microsoft Entra ID',                       unit: 'users',       group: 'Cloud' },
    // Cloud — Azure
    { value: 'azure-vm',                      label: 'Microsoft Azure VMs',                      unit: 'VMs',         group: 'Cloud',  matrixLabel: 'Microsoft Azure VM' },
    { value: 'azure-sql',                     label: 'Microsoft Azure SQL databases',            unit: 'databases',   group: 'Cloud' },
    { value: 'azure-cosmosdb',                label: 'Microsoft Azure Cosmos DB accounts',       unit: 'accounts',    group: 'Cloud' },
    { value: 'azure-files',                   label: 'Microsoft Azure file shares',              unit: 'shares',      group: 'Cloud' },
    { value: 'azure-vnet',                    label: 'Microsoft Azure virtual networks',         unit: 'VNets',       group: 'Cloud' },
    // Cloud — GCP
    { value: 'gcp-vm',                        label: 'Google Cloud VMs',                         unit: 'VMs',         group: 'Cloud',  matrixLabel: 'Google Cloud VM' },
    { value: 'gcp-sql',                       label: 'Google Cloud SQL instances',               unit: 'instances',   group: 'Cloud' },
    { value: 'gcp-spanner',                   label: 'Google Cloud Spanner instances',           unit: 'instances',   group: 'Cloud' },
    // Physical
    { value: 'agent-windows',               label: 'Microsoft Windows',         unit: 'endpoints',   group: 'Physical' },
    { value: 'agent-linux-kernel',          label: 'Linux (kernel module)',      unit: 'endpoints',   group: 'Physical' },
    { value: 'agent-linux-nosnap',          label: 'Linux (nosnap)',             unit: 'endpoints',   group: 'Physical' },
    { value: 'agent-aix',                   label: 'IBM AIX',                   unit: 'endpoints',   group: 'Physical' },
    { value: 'agent-solaris',               label: 'Oracle Solaris',             unit: 'endpoints',   group: 'Physical' },
    { value: 'agent-mac',                   label: 'Mac',                        unit: 'endpoints',   group: 'Physical' },
    { value: 'cdp-universal-agent',         label: 'CDP Universal Agent',        unit: 'endpoints',   group: 'Physical' },
    // Applications
    { value: 'plugin-ibm-db2',               label: 'IBM Db2',                unit: 'instances',   group: 'Applications' },
    { value: 'plugin-sql-server',            label: 'Microsoft SQL Server',   unit: 'instances',   group: 'Applications' },
    { value: 'plugin-oracle-rman',           label: 'Oracle RMAN',            unit: 'instances',   group: 'Applications' },
    { value: 'plugin-sap-oracle',            label: 'SAP on Oracle',          unit: 'instances',   group: 'Applications' },
    { value: 'plugin-sap-hana',              label: 'SAP HANA',               unit: 'instances',   group: 'Applications' },
    { value: 'plugin-sap-maxdb',             label: 'SAP MaxDB',              unit: 'instances',   group: 'Applications' },
    // SaaS
    { value: veeamBackupForMicrosoft365WorkloadId, label: 'Microsoft 365',                       unit: 'users',       group: 'SaaS' },
    // Containers
    { value: kastenK10WorkloadId,             label: 'Kubernetes clusters',                      unit: 'clusters',    group: 'Containers' },
];

// ── Pure query helpers ────────────────────────────────────────────────────────
export function getJobTypeDef(value) {
    return jobTypeOptions.find((t) => t.value === value) || null;
}

export function getJobWorkloadDef(value) {
    return jobWorkloadOptions.find((w) => w.value === value) || null;
}

/** Strip the unit suffix from a workload label when the unit is already shown separately in the chip qty row. */
export function stripWorkloadUnitSuffix(label, unit) {
    if (!label || !unit) return label;
    const suffix = ` ${unit}`;
    return label.endsWith(suffix) ? label.slice(0, -suffix.length).trimEnd() : label;
}

// ── Hypervisor migration helpers ──────────────────────────────────────────────
// These functions query `sites` and `hypervisorMigrationScopes` from state.js.
// sourceRef convention: "job:<swId>" = sourceWorkload with backup jobs (already protected),
//                       "uwl:<swId>" = sourceWorkload with no jobs (unprotected).

/** Returns source workloads with backup jobs across all sites that are hypervisor-type.
 *  Returned shape matches the legacy job object so hypervisor-migration.js callers
 *  (workloadType, customLabel, count, id, siteId, siteName) work unchanged. */
export function getHypervisorBackupJobsFromSites() {
    const hypervisorValues = new Set(
        jobWorkloadOptions.filter((w) => w.group === 'Hypervisors').map((w) => w.value)
    );
    return sites.flatMap((site) =>
        (Array.isArray(site.sourceWorkloads) ? site.sourceWorkloads : [])
            .filter((sw) => hypervisorValues.has(sw.workloadValue) && Array.isArray(sw.jobs) && sw.jobs.length > 0)
            .map((sw) => ({
                id:          sw.id,
                workloadType: sw.workloadValue,
                jobType:     'backup',
                count:       parseInt(sw.totalCount, 10) || 0,
                customLabel: sw.label || '',
                siteId:      site.id,
                siteName:    site.name,
            }))
    );
}

/** Returns source workloads WITHOUT jobs across all sites that are hypervisor-type.
 *  These represent VMs not yet backed up by Veeam — valid migration sources.
 *  Returned shape matches the legacy unprotected workload object so callers work unchanged. */
export function getHypervisorUnprotectedWorkloadsFromSites() {
    const hypervisorValues = new Set(
        jobWorkloadOptions.filter((w) => w.group === 'Hypervisors').map((w) => w.value)
    );
    return sites.flatMap((site) =>
        (Array.isArray(site.sourceWorkloads) ? site.sourceWorkloads : [])
            .filter((sw) => hypervisorValues.has(sw.workloadValue) && (!Array.isArray(sw.jobs) || sw.jobs.length === 0))
            .map((sw) => ({
                id:          sw.id,
                workloadType: sw.workloadValue,
                count:       parseInt(sw.totalCount, 10) || 0,
                customLabel: sw.label || '',
                siteId:      site.id,
                siteName:    site.name,
            }))
    );
}

/** Returns true if the given sourceWorkload id is referenced as a migration source in step 2. */
export function isJobHypervisorMigrationSource(swId) {
    return hypervisorMigrationScopes.some((s) => s.sourceRef === `job:${swId}`);
}

/** Remove migration scopes whose source sourceWorkload no longer exists in step 1. */
export function pruneStaleHypervisorMigrationScopes() {
    const protectedIds  = new Set(sites.flatMap((s) =>
        (Array.isArray(s.sourceWorkloads) ? s.sourceWorkloads : [])
            .filter((sw) => Array.isArray(sw.jobs) && sw.jobs.length > 0).map((sw) => sw.id)
    ));
    const unprotectedIds = new Set(sites.flatMap((s) =>
        (Array.isArray(s.sourceWorkloads) ? s.sourceWorkloads : [])
            .filter((sw) => !Array.isArray(sw.jobs) || sw.jobs.length === 0).map((sw) => sw.id)
    ));
    const before = hypervisorMigrationScopes.length;
    hypervisorMigrationScopes.splice(0, hypervisorMigrationScopes.length,
        ...hypervisorMigrationScopes.filter((s) => {
            if (!s.sourceRef) return true;
            if (s.sourceRef.startsWith('job:')) return protectedIds.has(parseInt(s.sourceRef.slice(4), 10));
            if (s.sourceRef.startsWith('uwl:')) return unprotectedIds.has(parseInt(s.sourceRef.slice(4), 10));
            return true;
        })
    );
    return hypervisorMigrationScopes.length !== before;
}

// ── VBR repository migration option arrays ────────────────────────────────────
// Source repository types for VBR backup repository migration to Veeam Hardened Repository (Step 2).
export const vbrRepoMigrationSourceTypeOptions = [
    { value: 'block',                 label: 'Block storage repository (local/SAN/DAS)' },
    { value: 'standalone',            label: 'Standalone backup repository' },
    { value: 'sobr-performance-tier', label: 'SOBR Performance Tier' },
];

// ── VB365 repository migration option arrays ──────────────────────────────────
// Source and target repository storage types for VB365 repository migration (Step 2).
export const vb365RepoTypeOptions = [
    { value: 'block',                label: 'Block storage (local/SAN/DAS)' },
    { value: 'object-s3',            label: 'Object storage — AWS S3' },
    { value: 'object-azure',         label: 'Object storage — Azure Blob' },
    { value: 'object-s3-compatible', label: 'Object storage — S3-compatible' },
    { value: 'smb',                  label: 'SMB share' },
];

// ── In-development workload tracking ─────────────────────────────────────────
export const inDevelopmentWorkloadStatusLabel = 'Coming Soon';

// Derived from the inDevelopment:true flag on each option — add the flag there, not here.
export const inDevelopmentWorkloadValues = new Set(
    workloadOptionGroups
        .flatMap((group) => group.options)
        .filter((option) => option.inDevelopment)
        .map((option) => option.value)
);
